import {
  datePl,
  formatMoney,
  KSEF_INVOICE_KINDS,
  KSEF_MODES,
  type KsefOverviewDto,
  type KsefSyncResultDto,
  PURCHASE_CATEGORIES,
  PURCHASE_FILTERS,
  type PurchaseDetailDto,
  type PurchaseFilter,
  type PurchaseListDto,
  type PurchaseRowDto,
  type SearchResultDto,
  todayInWarsaw,
  clampDateTo,
} from '@bqcrm/shared';
import { ActionIcon, Alert, Anchor, Autocomplete, Badge, Button, Card, CopyButton, Drawer, Group, Modal, Pagination, Select, SimpleGrid, Stack, Switch, Table, Text, Textarea, TextInput, Tooltip } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconCheck, IconCloudDownload, IconCopy, IconDownload, IconFileCode, IconSearch } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { CompanyBadgeById, PurchasePaymentBadge, PurchaseStatusBadge } from '../../components/Badges';
import { Money } from '../../components/Money';
import { QueryState } from '../../components/QueryState';
import { useCompanies } from '../../data';
import { EmptyState, SectionCard, FilterChips } from '../../components/ui';
import { PurchaseWarehouse } from './PurchaseWarehouse';
import { notifyError, notifyOk, notifyWarn } from '../../notify';
import { formatDateTime } from '../../format';
import { downloadCsv } from './csv';
import { InvoiceFleetSection } from '../fleet/InvoiceFleetSection';

const PAGE = 50;

function money(gr: number | null, currency: string) {
  if (gr === null) return '—';
  return currency === 'PLN' ? formatMoney(gr) : `${formatMoney(gr, false)} ${currency}`;
}

/** Pasek KSeF: stan pobierania każdej firmy i „Pobierz z KSeF”. */
function KsefBar({ companyId }: { companyId: string | null }) {
  const qc = useQueryClient();
  const companies = useCompanies();
  const q = useQuery({ queryKey: ['ksef-overview'], queryFn: () => api<KsefOverviewDto>('/finance/purchases/ksef') });
  const sync = useMutation({
    mutationFn: (id: string) => api<KsefSyncResultDto>('/finance/purchases/ksef/sync', { method: 'POST', body: { companyId: id } }),
    onSuccess: (r) => {
      if (r.error) notifyWarn(r.error, 'Pobieranie z KSeF nie powiodło się');
      else notifyOk(r.added ? `Pobrano nowe faktury: ${r.added}` : 'Brak nowych faktur w KSeF');
      void qc.invalidateQueries({ queryKey: ['ksef-overview'] });
      void qc.invalidateQueries({ queryKey: ['purchases'] });
    },
    onError: notifyError,
  });
  const rows = (q.data?.companies ?? []).filter((c) => !companyId || c.companyId === companyId);
  const active = rows.filter((c) => c.mode !== 'wylaczona');
  if (!q.data) return null;
  if (!active.length)
    return (
      <Alert color="gray" icon={<IconCloudDownload />} title="Faktury zakupu pobiera się z KSeF">
        {companyId ? 'Ta firma nie ma' : 'Żadna firma nie ma'} połączenia z KSeF.{' '}
        {q.data.canConfigure ? (
          <>
            Połącz je w{' '}
            <Anchor component={Link} to="/ustawienia/firmy">
              „Ustawienia → Firmy → KSeF”
            </Anchor>{' '}
            (token KSeF z uprawnieniem do przeglądania faktur).
          </>
        ) : (
          'Poproś właściciela o konfigurację połączenia.'
        )}
      </Alert>
    );
  return (
    <Card withBorder padding="sm">
      <Stack gap={6}>
        {active.map((c) => {
          const name = companies.data?.find((x) => x.id === c.companyId)?.shortName ?? '';
          const run = c.lastRun;
          return (
            <Group key={c.companyId} justify="space-between" wrap="wrap" gap="xs">
              <Group gap={8} wrap="wrap">
                <CompanyBadgeById id={c.companyId} />
                <Text size="sm" fw={600}>
                  {name}
                </Text>
                <Badge size="sm" variant="light" color={c.mode === 'produkcja' ? 'green' : 'yellow'}>
                  KSeF: {KSEF_MODES[c.mode]}
                </Badge>
                {c.status === 'blad' && (
                  <Badge size="sm" color="red" variant="light">
                    błąd połączenia
                  </Badge>
                )}
                <Text size="xs" c="dimmed">
                  {c.syncedTo ? `pobrano do ${formatDateTime(c.syncedTo)}` : 'jeszcze nie pobierano'}
                  {run ? ` · ostatnio ${formatDateTime(run.startedAt)} (${run.trigger === 'auto' ? 'automatycznie' : 'ręcznie'}${run.error ? ', błąd' : `, nowe: ${run.added}`})` : ''}
                </Text>
              </Group>
              {q.data.canSync && (
                <Button size="xs" variant="light" leftSection={<IconCloudDownload size={14} />} loading={sync.isPending && sync.variables === c.companyId} disabled={sync.isPending} onClick={() => sync.mutate(c.companyId)}>
                  Pobierz z KSeF
                </Button>
              )}
              {run?.error && (
                <Text size="xs" c="var(--app-text-red)" w="100%">
                  {run.error}
                </Text>
              )}
            </Group>
          );
        })}
        <Text size="xs" c="dimmed">
          Nowe faktury pobierają się też automatycznie co godzinę (KSeF ogranicza liczbę zapytań).
        </Text>
      </Stack>
    </Card>
  );
}

/** Finanse → Faktury zakupu: dokumenty z KSeF do sprawdzenia, zaksięgowania i zapłaty. */
export function Purchases({ companyId, initialFilter }: { companyId: string | null; initialFilter?: PurchaseFilter }) {
  const [text, setText] = useState('');
  const [search] = useDebouncedValue(text.trim(), 300);
  const [filter, setFilter] = useState<PurchaseFilter>(initialFilter ?? 'do_sprawdzenia');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rejected, setRejected] = useState(false);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);
  const params = (offset: number, limit: number) => {
    const p = new URLSearchParams({ filter, limit: String(limit), offset: String(offset) });
    if (search) p.set('q', search);
    if (companyId) p.set('companyId', companyId);
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (rejected) p.set('rejected', 'true');
    return p;
  };
  const key = params((page - 1) * PAGE, PAGE).toString();
  const q = useQuery({ queryKey: ['purchases', key], queryFn: () => api<PurchaseListDto>(`/finance/purchases?${key}`), placeholderData: (prev) => prev });
  useEffect(() => setPage(1), [search, filter, from, to, rejected, companyId]);
  const c = q.data?.counts;
  const s = q.data?.sums;

  const exportCsv = async () => {
    try {
      const rows: PurchaseRowDto[] = [];
      for (let offset = 0; offset < 10_000; offset += 200) {
        const part = await api<PurchaseListDto>(`/finance/purchases?${params(offset, 200)}`);
        rows.push(...part.items);
        if (rows.length >= part.total || !part.items.length) break;
      }
      downloadCsv(
        'faktury-zakupu.csv',
        ['Sprzedawca', 'NIP', 'Numer', 'Rodzaj', 'Data wystawienia', 'Netto', 'VAT', 'Brutto', 'Waluta', 'Termin', 'Zapłata', 'Data zapłaty', 'Stan', 'Kategoria', 'Sprawa', 'Numer KSeF'],
        rows.map((r) => [r.sellerName, r.sellerNip, r.invoiceNumber, KSEF_INVOICE_KINDS[r.kind] ?? r.kind, r.issueDate ?? '', r.netGr, r.vatGr, r.grossGr, r.currency, r.paymentDue ?? '', r.paymentStatus === 'zaplacona' ? 'zapłacona' : 'do zapłaty', r.paidAt ?? '', r.status, r.category, r.caseNumber ?? '', r.ksefNumber]),
      );
    } catch (e) {
      notifyError(e);
    }
  };

  return (
    <Stack gap="md">
      <KsefBar companyId={companyId} />
      <Group gap="sm" align="flex-end" wrap="wrap">
        <TextInput label="Szukaj" placeholder="Numer faktury, numer KSeF, sprzedawca, NIP, kategoria" leftSection={<IconSearch size={16} />} value={text} onChange={(e) => setText(e.currentTarget.value)} w={{ base: '100%', sm: 360 }} />
        <TextInput label="Wystawione od" type="date" value={from} onChange={(e) => { const v = e.currentTarget.value; setFrom(v); setTo(clampDateTo(v, to)); }} w={{ base: '48%', sm: 160 }} />
        <TextInput label="do" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.currentTarget.value)} w={{ base: '48%', sm: 160 }} />
        <Switch label="Pokaż odrzucone" checked={rejected} onChange={(e) => setRejected(e.currentTarget.checked)} mb={8} />
        <Button variant="default" leftSection={<IconDownload size={16} />} onClick={() => void exportCsv()} disabled={!q.data?.total}>
          Eksport do CSV
        </Button>
      </Group>
      <FilterChips
        value={filter}
        onChange={(v) => setFilter(v as PurchaseFilter)}
        options={(Object.keys(PURCHASE_FILTERS) as PurchaseFilter[]).map((k) => {
          const n = k === 'do_sprawdzenia' ? c?.doSprawdzenia : k === 'do_zaplaty' ? c?.doZaplaty : k === 'po_terminie' ? c?.poTerminie : undefined;
          return { value: k, label: n !== undefined ? `${PURCHASE_FILTERS[k]} (${n})` : PURCHASE_FILTERS[k] };
        })}
      />
      {s && (
        <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="sm">
          {[
            ['Netto', s.netGr, false],
            ['VAT (do odliczenia)', s.vatGr, false],
            ['Brutto', s.grossGr, false],
            ['Do zapłaty', s.toPayGr, false],
            ['Po terminie', s.overdueGr, s.overdueGr > 0],
          ].map(([label, v, alert]) => (
            <Card key={label as string} padding="sm" withBorder style={alert ? { borderColor: 'var(--mantine-color-red-4)' } : undefined}>
              <Text size="xs" c="dimmed">
                {label as string}
              </Text>
              <Text fw={700} className="tabular" c={alert ? 'var(--app-text-red)' : undefined}>
                <Money gr={v as number} />
              </Text>
            </Card>
          ))}
        </SimpleGrid>
      )}
      <Card padding={0}>
        <QueryState isLoading={q.isLoading} error={q.error}>
          <Table.ScrollContainer minWidth={1080}>
            <Table highlightOnHover verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Sprzedawca</Table.Th>
                  <Table.Th>Numer</Table.Th>
                  <Table.Th>Wystawiona</Table.Th>
                  <Table.Th ta="right">Netto</Table.Th>
                  <Table.Th ta="right">VAT</Table.Th>
                  <Table.Th ta="right">Brutto</Table.Th>
                  <Table.Th>Termin</Table.Th>
                  <Table.Th>Stan</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {q.data?.items.map((r) => (
                  <Table.Tr key={r.id} className="row-link" onClick={() => setOpenId(r.id)} style={r.overdue ? { backgroundColor: 'var(--mantine-color-red-light)' } : r.status === 'odrzucona' ? { opacity: 0.55 } : undefined}>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <CompanyBadgeById id={r.companyId} />
                        <Text size="sm" fw={600} lineClamp={1}>
                          {r.sellerName || '—'}
                        </Text>
                      </Group>
                      <Text size="xs" c="dimmed">
                        NIP {r.sellerNip || '—'}
                        {r.category ? ` · ${r.category}` : ''}
                        {r.caseNumber ? ` · ${r.caseNumber}` : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" style={{ whiteSpace: 'nowrap' }}>
                        {r.invoiceNumber || '—'}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {KSEF_INVOICE_KINDS[r.kind] ?? r.kind}
                        {r.mode !== 'produkcja' ? ' · test' : ''}
                        {r.hasError ? ' · błąd odczytu' : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{r.issueDate ? datePl(r.issueDate) : '—'}</Text>
                      {r.ksefReceivedAt && (
                        <Text size="xs" c="dimmed">
                          w KSeF {datePl(r.ksefReceivedAt)}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{money(r.netGr, r.currency)}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{money(r.vatGr, r.currency)}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" fw={600}>
                        {money(r.grossGr, r.currency)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c={r.overdue ? 'var(--app-text-red)' : undefined} fw={r.overdue ? 600 : 400}>
                        {r.paymentDue ? datePl(r.paymentDue) : '—'}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {r.paymentMethod}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <PurchaseStatusBadge status={r.status} />
                        {r.status !== 'odrzucona' && <PurchasePaymentBadge status={r.paymentStatus} overdue={r.overdue} />}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {!q.data?.items.length && (
            <EmptyState
              compact
              title={search ? 'Nie znaleziono faktur' : filter === 'do_sprawdzenia' ? 'Brak faktur do sprawdzenia' : filter === 'po_terminie' ? 'Brak faktur po terminie' : filter === 'do_zaplaty' ? 'Nic do zapłaty' : 'Brak faktur zakupu'}
              hint={search ? 'Sprawdź numer albo zmień filtry.' : 'Faktury, w których firma jest nabywcą, trafiają tu z KSeF.'}
            />
          )}
          {(q.data?.total ?? 0) > PAGE && (
            <Group justify="center" p="sm">
              <Pagination total={Math.ceil(q.data!.total / PAGE)} value={page} onChange={setPage} size="sm" />
            </Group>
          )}
        </QueryState>
      </Card>
      <PurchaseDrawer id={openId} onClose={() => setOpenId(null)} onOpen={setOpenId} />
    </Stack>
  );
}

/** Wybór sprawy (koszt sprawy) — wyszukiwanie po numerze lub kliencie. */
function CasePick({ value, label, onChange }: { value: string | null; label: string | null; onChange: (id: string | null) => void }) {
  const [text, setText] = useState('');
  const [term] = useDebouncedValue(text.trim(), 300);
  const q = useQuery({ queryKey: ['search', term], queryFn: () => api<SearchResultDto>(`/search?q=${encodeURIComponent(term)}`), enabled: term.length >= 2 });
  const data = (q.data?.cases ?? []).map((c) => ({ value: c.id, label: `${c.number} — ${c.clientName}` }));
  return (
    <Select
      label="Koszt sprawy"
      placeholder={label ?? 'Wpisz numer sprawy lub nazwisko klienta'}
      searchable
      clearable
      data={value && label && !data.some((d) => d.value === value) ? [{ value, label }, ...data] : data}
      value={value}
      searchValue={text}
      onSearchChange={setText}
      onChange={onChange}
      nothingFoundMessage={term.length >= 2 ? 'Nie znaleziono sprawy' : 'Wpisz co najmniej 2 znaki'}
      filter={({ options }) => options}
    />
  );
}

/** Szczegóły faktury zakupu: dane z XML, płatność, stawki, pozycje i praca księgowej. */
function PurchaseDrawer({ id, onClose, onOpen }: { id: string | null; onClose: () => void; onOpen: (id: string) => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['purchase', id], queryFn: () => api<PurchaseDetailDto>(`/finance/purchases/${id}`), enabled: !!id });
  const d = q.data;
  const [note, setNote] = useState('');
  const [category, setCategory] = useState('');
  const [paidAt, setPaidAt] = useState(todayInWarsaw());
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  useEffect(() => {
    setNote(d?.note ?? '');
    setCategory(d?.category ?? '');
  }, [d?.id, d?.note, d?.category]);
  const done = (msg: string) => (r: PurchaseDetailDto) => {
    qc.setQueryData(['purchase', id], r);
    void qc.invalidateQueries({ queryKey: ['purchases'] });
    notifyOk(msg);
  };
  const update = useMutation({ mutationFn: (body: Record<string, unknown>) => api<PurchaseDetailDto>(`/finance/purchases/${id}`, { method: 'PUT', body }), onError: notifyError });
  const pay = useMutation({ mutationFn: (body: { paid: boolean; paidAt: string | null }) => api<PurchaseDetailDto>(`/finance/purchases/${id}/payment`, { method: 'POST', body }), onError: notifyError });
  const edit = !!d?.can.edit;
  const setStatus = (status: string, msg: string, statusNote = '') => update.mutate({ status, statusNote }, { onSuccess: done(msg) });

  return (
    <Drawer opened={!!id} onClose={onClose} position="right" size="xl" title={d ? `${KSEF_INVOICE_KINDS[d.kind] ?? 'Faktura'} ${d.invoiceNumber}` : 'Faktura zakupu'}>
      <QueryState isLoading={q.isLoading} error={q.error}>
        {d && (
          <Stack gap="md">
            {d.mode !== 'produkcja' && (
              <Alert color="yellow" variant="light" py="xs">
                Dokument z {d.mode === 'demo' ? 'przedprodukcyjnego' : 'testowego'} środowiska KSeF — bez mocy prawnej.
              </Alert>
            )}
            {d.parseError && (
              <Alert color="red" variant="light" py="xs">
                Nie udało się odczytać części danych z XML ({d.parseError}). Dokument źródłowy jest zapisany — pobierz XML.
              </Alert>
            )}
            <Group gap="xs">
              <PurchaseStatusBadge status={d.status} />
              {d.status !== 'odrzucona' && <PurchasePaymentBadge status={d.paymentStatus} overdue={d.overdue} />}
              {d.statusByName && (
                <Text size="xs" c="dimmed">
                  {d.statusByName}, {d.statusAt ? formatDateTime(d.statusAt) : ''}
                  {d.statusNote ? ` — ${d.statusNote}` : ''}
                </Text>
              )}
            </Group>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
              <div>
                <Text size="xs" c="dimmed">
                  Sprzedawca
                </Text>
                <Text size="sm" fw={600}>
                  {d.sellerName}
                </Text>
                <Text size="sm">NIP {d.sellerNip}</Text>
                <Text size="sm" c="dimmed">
                  {d.sellerAddress}
                </Text>
              </div>
              <div>
                <Text size="sm">
                  <Text span c="dimmed">Wystawiona: </Text>
                  {d.issueDate ? datePl(d.issueDate) : '—'}
                  {d.saleDate && d.saleDate !== d.issueDate ? ` · sprzedaż ${datePl(d.saleDate)}` : ''}
                </Text>
                <Text size="sm">
                  <Text span c="dimmed">W KSeF: </Text>
                  {d.ksefReceivedAt ? formatDateTime(d.ksefReceivedAt) : '—'}
                </Text>
                <Text size="xs" c="dimmed" style={{ wordBreak: 'break-all' }}>
                  {d.ksefNumber}
                </Text>
                {(d.correctedNumber || d.correctedKsefNumber) && (
                  <Text size="sm">
                    <Text span c="dimmed">Koryguje: </Text>
                    {d.correctedNumber ?? d.correctedKsefNumber}
                  </Text>
                )}
              </div>
            </SimpleGrid>

            <SectionCard title="Płatność" padding="sm">
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                <Text size="sm">
                  <Text span c="dimmed">Do zapłaty: </Text>
                  <b>{money(d.grossGr, d.currency)}</b>
                </Text>
                <Text size="sm" c={d.overdue ? 'var(--app-text-red)' : undefined}>
                  <Text span c="dimmed">Termin: </Text>
                  {d.paymentDue ? datePl(d.paymentDue) : '—'}
                  {d.paymentMethod ? ` · ${d.paymentMethod}` : ''}
                </Text>
                {d.bankAccount && (
                  <Group gap={4} wrap="nowrap">
                    <Text size="sm">
                      <Text span c="dimmed">Rachunek: </Text>
                      {d.bankAccount.replace(/(\d{2})(?=\d)/, '$1 ').replace(/(\d{4})(?=\d)/g, '$1 ')}
                    </Text>
                    <CopyButton value={d.bankAccount}>
                      {({ copied, copy }) => (
                        <Tooltip label={copied ? 'Skopiowano' : 'Kopiuj numer rachunku'}>
                          <ActionIcon size="sm" variant="subtle" onClick={copy} aria-label="Kopiuj numer rachunku">
                            {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                )}
                {d.paidInDocument && <Text size="sm">Zapłacono przy wystawieniu (w dokumencie)</Text>}
                {d.paymentStatus === 'zaplacona' && d.paidAt && (
                  <Text size="sm">
                    <Text span c="dimmed">Zapłacono: </Text>
                    {datePl(d.paidAt)}
                    {d.paidByName ? ` (${d.paidByName})` : ''}
                  </Text>
                )}
              </SimpleGrid>
              {edit && d.status !== 'odrzucona' && (
                <Group gap="xs" mt="sm" align="flex-end">
                  {d.paymentStatus === 'do_zaplaty' ? (
                    <>
                      <TextInput label="Data zapłaty" type="date" size="xs" value={paidAt} onChange={(e) => setPaidAt(e.currentTarget.value)} w={160} />
                      <Button size="xs" loading={pay.isPending} onClick={() => pay.mutate({ paid: true, paidAt }, { onSuccess: done('Oznaczono jako zapłaconą') })}>
                        Oznacz jako zapłaconą
                      </Button>
                    </>
                  ) : (
                    <Button size="xs" variant="default" loading={pay.isPending} onClick={() => pay.mutate({ paid: false, paidAt: null }, { onSuccess: done('Cofnięto zapłatę') })}>
                      Cofnij zapłatę
                    </Button>
                  )}
                </Group>
              )}
            </SectionCard>

            <SectionCard title="Kwoty według stawek VAT" padding={0}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Stawka</Table.Th>
                    <Table.Th ta="right">Netto</Table.Th>
                    <Table.Th ta="right">VAT</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {d.vatLines.map((l) => (
                    <Table.Tr key={l.rate}>
                      <Table.Td>{l.rate}</Table.Td>
                      <Table.Td ta="right">{money(l.netGr, d.currency)}</Table.Td>
                      <Table.Td ta="right">{money(l.vatGr, d.currency)}</Table.Td>
                    </Table.Tr>
                  ))}
                  <Table.Tr>
                    <Table.Td fw={700}>Razem (brutto {money(d.grossGr, d.currency)})</Table.Td>
                    <Table.Td ta="right" fw={700}>
                      {money(d.netGr, d.currency)}
                    </Table.Td>
                    <Table.Td ta="right" fw={700}>
                      {money(d.vatGr, d.currency)}
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </SectionCard>

            {d.items.length > 0 && (
              <SectionCard title={`Pozycje (${d.items.length})`} padding={0}>
                <Table.ScrollContainer minWidth={560}>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Nazwa</Table.Th>
                        <Table.Th ta="right">Ilość</Table.Th>
                        <Table.Th ta="right">Cena netto</Table.Th>
                        <Table.Th ta="right">VAT</Table.Th>
                        <Table.Th ta="right">Wartość netto</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {d.items.map((it) => (
                        <Table.Tr key={it.lp}>
                          <Table.Td>{it.name}</Table.Td>
                          <Table.Td ta="right">
                            {it.quantity ?? '—'} {it.unit}
                          </Table.Td>
                          <Table.Td ta="right">{money(it.unitNetGr, d.currency)}</Table.Td>
                          <Table.Td ta="right">{it.vatRate}</Table.Td>
                          <Table.Td ta="right">{money(it.netGr, d.currency)}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </SectionCard>
            )}

            {/* Flota (docs/19 §7): przypisanie pozycji / części faktury do pojazdów — gdy moduł włączony i jest uprawnienie do kosztów */}
            <InvoiceFleetSection invoiceId={d.id} />

            {d.related.length > 0 && (
              <SectionCard title="Powiązane dokumenty" padding="sm">
                <Stack gap={4}>
                  {d.related.map((r) => (
                    <Anchor key={r.id} component="button" type="button" size="sm" ta="left" onClick={() => onOpen(r.id)}>
                      {KSEF_INVOICE_KINDS[r.kind] ?? r.kind} {r.invoiceNumber}
                    </Anchor>
                  ))}
                </Stack>
              </SectionCard>
            )}

            <PurchaseWarehouse invoiceId={d.id} companyId={d.companyId} />

            <SectionCard title="Obsługa" padding="sm">
              <Stack gap="sm">
                {edit && (
                  <Group gap="xs">
                    {d.status === 'nowa' && (
                      <Button size="xs" loading={update.isPending} onClick={() => setStatus('zaakceptowana', 'Zaakceptowano fakturę')}>
                        Zaakceptuj
                      </Button>
                    )}
                    {(d.status === 'nowa' || d.status === 'zaakceptowana') && (
                      <Button size="xs" color="green" variant={d.status === 'zaakceptowana' ? 'filled' : 'light'} loading={update.isPending} onClick={() => setStatus('zaksiegowana', 'Oznaczono jako zaksięgowaną')}>
                        Oznacz jako zaksięgowaną
                      </Button>
                    )}
                    {d.status !== 'nowa' && (
                      <Button size="xs" variant="default" loading={update.isPending} onClick={() => setStatus('nowa', 'Przywrócono do sprawdzenia')}>
                        Przywróć do sprawdzenia
                      </Button>
                    )}
                    {d.status !== 'odrzucona' && (
                      <Button size="xs" color="red" variant="light" onClick={() => setRejecting(true)}>
                        Odrzuć
                      </Button>
                    )}
                  </Group>
                )}
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  <Autocomplete label="Kategoria kosztu" data={[...PURCHASE_CATEGORIES]} value={category} onChange={setCategory} disabled={!edit} />
                  {edit ? (
                    <CasePick value={d.caseId} label={d.caseNumber} onChange={(caseId) => update.mutate({ caseId }, { onSuccess: done(caseId ? 'Przypisano do sprawy' : 'Odpięto od sprawy') })} />
                  ) : (
                    <Text size="sm">
                      <Text span c="dimmed">Sprawa: </Text>
                      {d.caseId ? (
                        <Anchor component={Link} to={`/sprawy/${d.caseId}`} size="sm">
                          {d.caseNumber}
                        </Anchor>
                      ) : (
                        '—'
                      )}
                    </Text>
                  )}
                </SimpleGrid>
                {d.caseId && edit && (
                  <Anchor component={Link} to={`/sprawy/${d.caseId}`} size="sm">
                    Otwórz sprawę {d.caseNumber}
                  </Anchor>
                )}
                <Textarea label="Notatka" autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} disabled={!edit} />
                <Group justify="space-between">
                  <Button size="xs" variant="subtle" component="a" href={`/api/finance/purchases/${d.id}/xml`} leftSection={<IconFileCode size={14} />}>
                    Pobierz XML z KSeF
                  </Button>
                  {edit && (
                    <Button size="xs" disabled={note === d.note && category === d.category} loading={update.isPending} onClick={() => update.mutate({ note, category }, { onSuccess: done('Zapisano') })}>
                      Zapisz
                    </Button>
                  )}
                </Group>
              </Stack>
            </SectionCard>
          </Stack>
        )}
      </QueryState>
      <Modal opened={rejecting} onClose={() => setRejecting(false)} title="Odrzucenie faktury zakupu">
        <Stack>
          <Text size="sm">Odrzucona faktura nie liczy się do płatności ani kosztów. Dokument zostaje w systemie (KSeF go nie usuwa).</Text>
          <Textarea label="Powód" placeholder="np. duplikat, nie nasz zakup, oczekujemy korekty" value={reason} onChange={(e) => setReason(e.currentTarget.value)} autosize minRows={2} data-autofocus />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRejecting(false)}>
              Anuluj
            </Button>
            <Button
              color="red"
              disabled={reason.trim().length < 3}
              loading={update.isPending}
              onClick={() =>
                update.mutate(
                  { status: 'odrzucona', statusNote: reason.trim() },
                  {
                    onSuccess: (r) => {
                      done('Odrzucono fakturę')(r);
                      setRejecting(false);
                      setReason('');
                    },
                  },
                )
              }
            >
              Odrzuć fakturę
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Drawer>
  );
}
